import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

export interface ConversationListItemData {
  id: string;
  title: string;
  preview: string | undefined;
  /** Raw ISO 8601 timestamp — formatted to relative time at render in the View layer */
  updated_at: string | undefined;
  /** Whether the agent is currently processing this conversation */
  processing_status: 'idle' | 'processing';
  /** Whether this conversation has unread messages (server-derived) */
  unread: boolean;
  /**
   * Creator Mode: when true, this conversation's claims schedule against the
   * RW-repo warm-pool. The agent can author skills, sub-agents, and slash
   * commands; writes commit back to git on session end.
   */
  creator_mode: boolean;
  /**
   * Whether the title was set manually and is locked against LLM auto-titling.
   * When true, the auto-title client updater leaves it alone.
   */
  title_locked: boolean;
}

function toItem(c: {
  id: string;
  title: string;
  last_message_preview?: string | null;
  updated_at: string;
  processing_status?: 'idle' | 'processing';
  unread?: boolean;
  creator_mode?: boolean;
  title_locked?: boolean;
}): ConversationListItemData {
  return {
    id: c.id,
    title: c.title || 'New Conversation',
    preview: c.last_message_preview ?? undefined,
    updated_at: c.updated_at,
    processing_status: c.processing_status ?? 'idle',
    unread: c.unread ?? false,
    creator_mode: c.creator_mode ?? false,
    title_locked: c.title_locked ?? false,
  };
}

export function useConversations() {
  const apiClient = useApiClient();
  const [conversations, setConversations] = useState<ConversationListItemData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    setStatus('loading');
    apiClient
      .listConversations({ limit: '25' })
      .then((res) => {
        setConversations(res.data.map(toItem));
        setHasMore(res.has_more);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        setStatus('error');
      });
  }, [apiClient, fetchCount]);

  const reload = useCallback(() => setFetchCount((n) => n + 1), []);

  const createConversation = useCallback(async (title?: string) => {
    try {
      const conv = await apiClient.createConversation(title ? { title } : {});
      setConversations((prev) => [toItem(conv), ...prev]);
      setSelectedId(conv.id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
    }
  }, [apiClient]);

  const archiveConversation = useCallback(async (id: string) => {
    try {
      await apiClient.updateConversation(id, { status: 'archived' });
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive conversation');
    }
  }, [apiClient]);

  const selectConversation = useCallback((id: string | null) => setSelectedId(id), []);

  const markRead = useCallback(async (id: string) => {
    // Optimistic update — set unread to false immediately
    setConversations((prev) => prev.map((c) => c.id === id ? { ...c, unread: false } : c));
    // Fire-and-forget API call
    apiClient.markConversationRead(id).catch(() => {});
  }, [apiClient]);

  const loadMore = useCallback(async () => {
    const last = conversations[conversations.length - 1];
    if (!last) return;
    try {
      const res = await apiClient.listConversations({ limit: '25', starting_after: last.id });
      setConversations((prev) => [...prev, ...res.data.map(toItem)]);
      setHasMore(res.has_more);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load more conversations');
    }
  }, [apiClient, conversations]);

  const refreshConversation = useCallback(async (id: string) => {
    try {
      const conv = await apiClient.getConversation(id);
      setConversations((prev) => prev.map((c) => c.id === id ? toItem(conv) : c));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to refresh conversation');
    }
  }, [apiClient]);

  // Reflect the LLM's auto-generated title in the list. No-ops when the title was
  // manually set (title_locked) so a late auto-title race can't clobber the UI.
  const updateConversationTitle = useCallback((id: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) =>
        c.id === id && !c.title_locked && c.title === 'New Conversation' ? { ...c, title } : c,
      ),
    );
  }, []);

  /**
   * Rename a conversation manually. Optimistically updates the list title and
   * marks it `title_locked` so the LLM never overwrites it again, then PATCHes
   * the server with `{ title, title_locked: true }`. Rolls back on failure.
   * No-ops on an empty/whitespace title.
   */
  const renameConversation = useCallback(async (id: string, rawTitle: string) => {
    const title = rawTitle.trim();
    if (!title) return;
    let prevSnapshot: ConversationListItemData | undefined;
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        prevSnapshot = c;
        return { ...c, title, title_locked: true };
      }),
    );
    try {
      await apiClient.updateConversation(id, { title, title_locked: true });
    } catch (err: unknown) {
      // Roll back to the pre-edit title/lock on failure.
      if (prevSnapshot) {
        const snap = prevSnapshot;
        setConversations((prev) => prev.map((c) => (c.id === id ? snap : c)));
      }
      setError(err instanceof Error ? err.message : 'Failed to rename conversation');
    }
  }, [apiClient]);

  /**
   * Flip Creator Mode on/off for a conversation.
   *
   * Optimistically updates local state, then PATCHes the conversation on
   * the server. Subsequent messages on this conversation route to the
   * RW-repo warm-pool when `enabled=true`, the default RO pool otherwise.
   *
   * The agent picks up the new repo posture on the NEXT message, not the
   * one currently in flight — Creator Mode is read at claim-create time.
   */
  const setCreatorMode = useCallback(async (id: string, enabled: boolean) => {
    // Optimistic local update so the toggle UI feels snappy.
    setConversations((prev) =>
      prev.map((c) => c.id === id ? { ...c, creator_mode: enabled } : c),
    );
    try {
      await apiClient.updateConversation(id, { creator_mode: enabled });
    } catch (err: unknown) {
      // Roll back the optimistic update on failure.
      setConversations((prev) =>
        prev.map((c) => c.id === id ? { ...c, creator_mode: !enabled } : c),
      );
      setError(err instanceof Error ? err.message : 'Failed to update creator mode');
    }
  }, [apiClient]);

  return { status, error, conversations, createConversation, archiveConversation, selectedId, selectConversation, hasMore, loadMore, refreshConversation, updateConversationTitle, renameConversation, setCreatorMode, markRead, reload };
}
